import { ShowIf } from '@ztechapps/webui.util'
import isEmpty from 'lodash/isEmpty'
import type { ReactNode } from 'react'
import React from 'react'
import { Helmet } from 'react-helmet'

export type PageTitleProps = {
  /**
   * The main title of the page
   * @example 'Login' will render as 'miniWarp - Login' in the browser tab (default siteName)
   */
  title?: ReactNode

  /**
   * Optional description for SEO meta tags
   */
  description?: string

  /**
   * Optional keywords for SEO meta tags
   */
  keywords?: string[]

  /**
   * Optional site name for the title prefix. Apps with their own brand (e.g. the panelpunches storefront)
   * should pass an explicit `siteName` via their own wrapper component.
   * @default 'miniWarp'
   */
  siteName?: string

  /**
   * Optional children to render instead of the default h1 title
   */
  children?: ReactNode

  /**
   * If true, won't render the default h1 title
   * @default false
   */
  noTitleTag?: boolean
}

/**
 * A reusable component for managing page titles and SEO meta tags
 *
 * @example
 * // Basic usage
 * <PageTitle title="Login" />
 *
 * @example
 * // With custom meta description and keywords
 * <PageTitle
 *   title="Product Catalog"
 *   description="Browse our wide range of panel punches and metal fabrication tools"
 *   keywords={['panel punches', 'metal tools', 'fabrication']}
 * />
 *
 * @example
 * // With custom content and no default h1
 * <PageTitle title="Dashboard" noTitleTag>
 *   <div className="page-header">
 *     <h1>Dashboard</h1>
 *     <Breadcrumbs />
 *   </div>
 * </PageTitle>
 */
export const PageTitle = ({
  title,
  description = '',
  keywords = [],
  siteName = 'miniWarp',
  children,
  noTitleTag = false,
}: PageTitleProps) => {
  // Format the full title with site name
  const stringTitle = title ? String(title) : ''
  const fullTitle = stringTitle ? `${siteName} - ${stringTitle}` : siteName
  const hasMetaData = !isEmpty(description) || !isEmpty(keywords) || !isEmpty(siteName) || !isEmpty(stringTitle)

  return (
    <>
      <ShowIf condition={hasMetaData}>
        <Helmet>
          <title>{fullTitle}</title>
          <meta name="description" content={description} />
          {!isEmpty(keywords) && <meta name="keywords" content={keywords.join(', ')} />}

          {/* Open Graph / Facebook */}
          <meta property="og:type" content="website" />
          <meta property="og:title" content={fullTitle} />
          <meta property="og:description" content={description} />
          <meta property="og:site_name" content={siteName} />

          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={fullTitle} />
          <meta name="twitter:description" content={description} />
        </Helmet>
      </ShowIf>

      <ShowIf condition={!noTitleTag && !children && Boolean(title)}>
        <div>
          <h4>{title ?? 'Overview'}</h4>
        </div>
      </ShowIf>

      {children}
    </>
  )
}
